export * from './firestore'
export * from './slideDeckAccess'
import type { FirestoreRoomStateAnswer } from '../firestore/RoomStateAnswer/schema'
import type { FirestoreRoomStateSummary } from '../firestore/RoomStateSummary/schema'
import type { PublicUser } from '../models/PublicUser'
import type { FirestoreRoomMessage } from '../firestore/RoomMessage/schema'
import type { FirestorePublicUser } from '../firestore/PublicUser/schema'
import {
  FirestoreRoomMessageType,
  FirestoreRoomMessageStatus,
} from '../firestore/RoomMessage/schema'
import type { MobxDocument } from '../firestore-mobx'
import type { StaticModelCollection } from '../firestore-mobx/model'
import {
  SlideDeckMaterialType,
  SlideDeckMaterialWorkType,
} from '../firestore/SlideDeckMaterial/schema'
import type { MaterialRequiredFieldsForUpload } from '../firestore/SlideDeckMaterial'
import type { ExhibitFieldsForUpload } from '../firestore/SlideDeckExhibit'
import type { AuthorFieldsForUpload } from '../firestore/SlideDeckAuthor'
import type { QuestionFieldsForUpload } from '../firestore/SlideQuestion'
import type { SlideFieldsForUpload } from '../firestore/Slide'
import type { ExperienceDetailsForUpload } from '../firestore/SlideDeck'
import {
  ExhibitGridDisplayMode,
  SlideType,
  UnsupportedMediaTypeError,
} from '../firestore/Slide/types'
import {
  RoomStateDemoType,
  RoomStateVideoMethod,
} from '../firestore/RoomState/types'
import { UserProfileRole } from '../firestore/UserProfile/types'
import type { FirestoreTeachingPlan } from '../firestore/TeachingPlan/schema'
import type { FirestoreOrganization } from '../firestore/Organization/schema'
import type { TeachingPlanDetailsPayload } from '../firestore/TeachingPlan/types'
import { TeachingPlanUploadType } from '../firestore/TeachingPlan/types'
import {
  WrappedFirestoreError,
  StorageFetchError,
} from '../firestore-mobx/errors'
import { TeachingPlanStatus } from '../firestore/TeachingPlan/types'
import { PromotionSource } from '../firestore/UserPromotion/types'
import { PromotionType } from '../firestore/Promotion/types'
import {
  OrganizationInvoiceStatus,
  OrganizationState,
} from '../firestore/Organization/schema'
import {
  SectionState,
  SectionPaymentMethod,
  type FirestoreSection,
} from '../firestore/Section/schema'
import { AssignmentState } from '../firestore/SectionAssignment/schema'
import {
  AssignmentGroupingType,
  AssignmentType,
} from '../firestore/SectionAssignment/schema'
import type { FirestoreSectionAssignment } from '../firestore/SectionAssignment/schema'
import type { FirestoreOrganizationInstructor } from '../firestore/OrganizationInstructor/schema'
import {
  SectionRequestState,
  SectionRequestType,
} from '../firestore/SectionRequest/schema'
import {
  BloomScore,
  PassFailScore,
} from '../firestore/RoomStateRubricResult/schema'
import type { FirestoreRoomStateRubricResultDetail } from '../firestore/RoomStateRubricResultDetail/schema'
import type { FirestoreRoomStateEvent } from '../firestore/RoomStateEvent/schema'
import type { InstitutionIntegrationCanvasSettings } from '../firestore/InstitutionIntegration/schema'
import { InstitutionIntegrationType } from '../firestore/InstitutionIntegration/types'
import { SuperAdminPrivilege } from '../firestore/SettingsSuperAdmin/types'
import type {
  FirestoreMedia,
  FirestoreVideoMedia,
  FirestoreImageMedia,
} from '../firestore/Media/schema'
import { UserProfileNotificationType } from '../firestore/UserProfileNotification/schema'
import type { NotificationGroup } from '../util/notifications'
import type { SystemMessage } from '../firestore/RoomMessage/schema'
import type { RoomState } from '../models/RoomState'
import { ServerIdentities, ServerIdentitiesList } from '../util/identities'
import type { Section, SectionStatus } from '../models/Section'
import type { SectionAssignment } from '../models/SectionAssignment'
import {
  RubricType,
  RubricTypeList,
  RubricTypeMap,
  RubricTypeNames,
  RubricTypeReverseMap,
} from '../firestore/PromptRubricType/types'
import {
  PromptType,
  PromptVersionStatus,
} from '../firestore/PromptVersion/types'
import type { FirestoreOrganizationCustomProduct } from '../firestore/OrganizationCustomProduct/schema'
import { PostAssignmentPromptType } from '../firestore/PromptPostAssignmentType/types'
import { type AdminSlideDeckPublishValidationWarning } from '../util/slideDeckValidation'
import {
  PromptVersionInteractiveElementValidModels,
  PromptVersionInteractiveElementValidReasoningEffort,
} from '../firestore/PromptInteractiveElementType/types'

import {
  SupportedComponents,
  SupportedLayoutComponents,
} from '../cubits/meeting/InteractiveForm/types'

import { formSchema } from '../cubits/meeting/InteractiveForm/schema'
import type {
  InteractiveFormProperty,
  InteractiveFormComponentProps,
  InteractiveFormGroupComponentProps,
  InteractiveFormPropertyGroup,
} from '../cubits/meeting/InteractiveForm/schema'
import { SlideQuestionType } from '../models/SlideQuestionType'
import { ClickWrapRole, ClickWrapState } from '../firestore/ClickWrap/schema'
import type { FirestoreOrganizationSectionPassCouponFolder } from '../firestore/OrganizationSectionPassCouponFolder/schema'
import type { FirestoreOrganizationSectionPassCoupon } from '../firestore/OrganizationSectionPassCoupon/schema'
import type { RoleFields } from '../firestore/SlideDeckRole/types'
import { RoomStateStatus } from '../firestore/RoomState/types'
import {
  type SlideDeckAccessError,
  type SlideDeckInclude,
  type GetSlideDeckForAssignmentResult,
  slideDeckIncludeEnum,
} from './slideDeckAccess'

export {
  PromptVersionInteractiveElementValidModels,
  PromptVersionInteractiveElementValidReasoningEffort,
  AdminSlideDeckPublishValidationWarning,
  FirestoreMedia,
  FirestoreVideoMedia,
  FirestoreImageMedia,
  FirestoreRoomStateAnswer,
  FirestoreRoomStateSummary,
  FirestoreRoomMessage,
  FirestorePublicUser,
  FirestoreSectionAssignment,
  FirestoreOrganization,
  FirestoreOrganizationSectionPassCouponFolder,
  FirestoreOrganizationSectionPassCoupon,
  StaticModelCollection,
  MobxDocument,
  MaterialRequiredFieldsForUpload,
  FirestoreRoomStateEvent,
  FirestoreRoomStateRubricResultDetail,
  ExhibitFieldsForUpload,
  AuthorFieldsForUpload,
  QuestionFieldsForUpload,
  SlideFieldsForUpload,
  ExperienceDetailsForUpload,
  FirestoreTeachingPlan,
  TeachingPlanDetailsPayload,
  InstitutionIntegrationCanvasSettings,
  NotificationGroup,
  SystemMessage,
  RoomStateStatus,
  AssignmentType,
  AssignmentGroupingType,
}

export type { RoleFields }

export {
  InstitutionIntegrationType,
  FirestoreRoomMessageType,
  FirestoreRoomMessageStatus,
  SlideDeckMaterialType,
  SlideDeckMaterialWorkType,
  ExhibitGridDisplayMode,
  SlideType,
  RoomStateVideoMethod,
  UserProfileRole,
  TeachingPlanUploadType,
  WrappedFirestoreError,
  StorageFetchError,
  TeachingPlanStatus,
  PromotionSource,
  PromotionType,
  OrganizationInvoiceStatus,
  OrganizationState,
  SectionState,
  SectionPaymentMethod,
  AssignmentState,
  BloomScore,
  PassFailScore,
  SectionRequestState,
  SectionRequestType,
  SuperAdminPrivilege,
  UserProfileNotificationType,
  ServerIdentities,
  ServerIdentitiesList,
  RubricType,
  RubricTypeList,
  RubricTypeNames,
  RubricTypeMap,
  RubricTypeReverseMap,
  PromptType,
  PostAssignmentPromptType,
  PromptVersionStatus,
  RoomStateDemoType,
  SlideQuestionType,
  ClickWrapRole,
  ClickWrapState,
}

export {
  SupportedComponents,
  SupportedLayoutComponents,
  InteractiveFormProperty,
  InteractiveFormComponentProps,
  InteractiveFormGroupComponentProps,
  InteractiveFormPropertyGroup,
  formSchema,
}

export type {
  SlideDeckInclude,
  SlideDeckAccessError,
  GetSlideDeckForAssignmentResult,
}
export { slideDeckIncludeEnum }

export enum InstructorSectionView {
  cases,
  students,
}

export enum AssignmentStatus {
  unconfigured = -1,
  draft = 0,
  pending = 1,
  in_progress = 2,
  completed = 3,
  canceled = 4,
}

// TODO: Remove when newInstructorPages flag is removed
export enum AssignmentStatusOld {
  pending = 0,
  completed = 1,
}

export enum InvitationConsumeResult {
  errorAlreadyConsumed = 1,
  errorCreatedByYou = 2,
  errorInvitationNotFound = 3,
  errorInvitationExpired = 4,
  success = 5,
  errorPromotionAlreadyConsumed = 6,
  errorNonLTIUserJoiningLTISection = 7,
  errorLTIUserJoiningNonLTISection = 8,
  errorAlreadyEnrolled = 9,
}

export enum LibraryObjectState {
  // you are in a room and it is running
  live,
  // you are in a room and we are near the scheduled time
  readyToStart,
  // you have been assigned to a slideDeck but not joined a room
  // but have not paid for the assignment
  invited,
  // you have been assigned to a slideDeck but not joined a room
  // and have paid for the assignment
  enrolled,
  // you are groupLeader and have not scheduled a room
  mustSchedule,
  // you are groupLeader and have scheduled a room
  scheduled,
  // you are in a room that is not scheduled and you are not groupLeader
  pending,
  // you are in a room that is completed
  completed,
  // your assignment has expired and the room is incomplete
  expired,
  // assignment as started, but can be resumed
  suspended,
  // assignment cancelled
  canceled,
}

// Define media type enum
export enum MediaType {
  Video = 0,
  Image = 1,
  Pdf = 2,
}

export enum MaterialWorkType {
  neither = 0,
  preWork = 1,
  postWork = 2,
}

export enum MaterialType {
  url = 0,
  pdf = 1,
  mp3 = 2,
  webm = 3,
  featuredLarge = 4,
  jpg = 5,
}

// All possible message types.
export enum ChatMessageType {
  Audio = 'audio',
  Custom = 'custom',
  File = 'file',
  Image = 'image',
  System = 'system',
  Text = 'text',
  Unsupported = 'unsupported',
  Video = 'video',
}

// Base interface for a message
export interface ChatMessage {
  authorId: string
  author: PublicUser
  id: string
  text: string
  type: FirestoreRoomMessageType
  createdAt?: Date
  metadata?: Record<string, unknown>
  remoteId?: string
  repliedMessage?: ChatMessage
  roomId?: string
  showStatus?: boolean
  status?: FirestoreRoomMessageStatus
  nudgeSent?: boolean
  updatedAt?: Date
  firstFromUser: boolean
  showAvatar: boolean
  timestamp: Date
  isCurrentUserMessage: boolean
  seenBy: FirestoreRoomMessage['seenBy']
  isSystemMessage: boolean
  systemMessagePayload?: SystemMessage
}

export type MeetingSidebarTab = 'peers' | 'exhibits' | 'agenda' | 'chat'

export enum InvitationType {
  oneTime = 0,
  multiUse = 1,
}

/**
 * wraps firestore document data and adds an id field
 */
export type WithID<T> = T & { id: string }

export enum BroadcastState {
  uninitialized = 'uninitialized',
  initialized = 'initialized',
  playing = 'playing',
  paused = 'paused',
  stopped = 'stopped',
}

export type TranscriptMessage = {
  transcript: string
  final: boolean
  identity: string
  id: number
  finalSort?: number
  isChat?: boolean
}

export type FirestoreInvitation = {
  userId: string
  sectionId: string
  expiresAt: { seconds: number; nanoseconds: number }
  promotionId?: string
  promotionArguments?: {
    collisionCode: string
  }
}

export enum SessionMuting {
  none = 'none',
  all = 'all',
  groupLeader = 'groupLeader',
}

export enum SlideDeckState {
  deleted = -2,
  uninitialized = -1,
  draft = 0,
  published = 1,
  hidden = 2,
}

export enum SlideDeckType {
  original = 0,
  dialog = 1,
  simulation = 2,
  negotiation = 3,
  demo = 4,
  oral_exam = 5,
  tutorial = 6,
}

export enum SlideDeckReferenceType {
  alternativeTo = 0,
  basedOn = 1,
  supplementTo = 2,
}

export class DocumentDoesNotExistError extends Error {
  constructor() {
    super('Document does not exist')
  }
}

export interface ImpersonationInfo {
  userId: string
  organizationId?: string
  hideBanner?: boolean
}

export type OrganizationInstructorDetails = {
  publicUser: PublicUser
  orgInstructor: FirestoreOrganizationInstructor
}

export enum MeetingSessionPhase {
  avcheck = 'avcheck',
  meeting = 'meeting',
}

export const promptVersionPromptFields = [
  'primaryPrompt',
  'outputPrompt',
  'reminderPrompt',
  'statsPrompt',
  'tool0Description',
  'tool1Description',
  'tool2Description',
  'model',
  'reasoningEffort',
] as const

export const promptTypeFieldLabels = [
  'tool0Description',
  'tool1Description',
  'tool2Description',
] as const

export type CheckIsValidInvitationInvalidReason =
  | 'invitationDoesNotExist'
  | 'invitationConsumedByCurrentUser'
  | 'invitationCreatedByCurrentUser'
  | 'invitationExpired'
  | 'oneTimeInvitationAlreadyConsumed'
  | 'sectionDoesNotExist'
  | 'userRemovedFromSection'
  | 'userCollaboratorOnSection'
  | 'userSectionInstructor'
  | 'userStudentInSection'
  | 'internal'
  | 'ltiUserAcceptingNonLTISectionInvitation'
  | 'nonLTIUserAcceptingLTISectionInvitation'

// we want to share this between functions / repository
export type CheckIsValidInvitationResult =
  | {
      isValid: false
      invalidReason: CheckIsValidInvitationInvalidReason
      invitationType?:
        | null
        | 'section'
        | 'instructor'
        | 'ta'
        | 'slideDeckReview'
    }
  | {
      isValid: true
      invitationType: null | 'instructor' | 'ta' | 'slideDeckReview'
    }
  | {
      isValid: true
      invitationType: 'section'
      /** only **className** and **sectionName** are real values, rest are stubs from empty model*/
      section: FirestoreSection
      /** only **firstName**, **lastName**, **imageURL** are real values, rest are stubs from empty model*/
      instructor: FirestorePublicUser
    }

export type FetchOrganizationCustomProductsForInstructorArgs = {
  organizationId: string
  instructorId: string
}

export type FetchOrganizationCustomProductsForInstructorResult =
  FirestoreOrganizationCustomProduct[]

export type RoomStateWithStatus = RoomState & { status: RoomStateStatus }

export type RoomStateWithStatusAndRoleIssueAndMinGroupSizeIssue = RoomState & {
  status: RoomStateStatus
  roleIssue: RoleAssignmentIssue | null
  minGroupSizeIssue: MinGroupSizeIssue | null
}

export type RoleAssignmentIssue =
  | 'belowMinUsers'
  | 'aboveMaxUsers'
  | 'invalidRoles'
  | 'unassignedUsers'

export type MinGroupSizeIssue = 'belowMinGroupSize'

/** Since it's difficult to annotate classes directly, we put the class one level deep at the cost of sometimes writing section.section.  */
export type SectionWithStatusAndAssignments = {
  section: Section
  sectionStatus: SectionStatus
  assignments: SectionAssignment[]
}

export { UnsupportedMediaTypeError }
